import { notifier } from "../../core/notifier/notifier";
import { translator } from "../../translation/translator";
import { ScrollLock } from "../../core/scroll-lock";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import type * as z from "zod";
import {
    UploadAvatar200Response,
    UploadAvatar400Response,
    UploadAvatar403Response,
    UploadAvatar422Response,
    UploadAvatar500Response,
} from "../../api/generated/users.zod";
import { AvatarApi } from "./avatar-api";
import { AvatarListener } from "./avatar-listener";
import { AvatarRender } from "./avatar-render";
import { avatarForm } from "./avatar-form";

type AvatarResponse = z.infer<typeof UploadAvatar200Response>;
type AvatarErrorResponse =
    | z.infer<typeof UploadAvatar400Response>
    | z.infer<typeof UploadAvatar403Response>
    | z.infer<typeof UploadAvatar422Response>
    | z.infer<typeof UploadAvatar500Response>;

interface BootstrapModalInstance {
    hide?: () => void;
}

interface BootstrapModalApi {
    getInstance?: (el: Element) => BootstrapModalInstance | null;
}

interface BootstrapGlobal {
    Modal?: BootstrapModalApi;
}

export class AvatarHandler {
    public userId: number;

    public modal: HTMLElement;

    public avatarInput: HTMLInputElement | null;

    public avatarPreview: HTMLImageElement | null;

    public saveAvatarBtn: HTMLButtonElement | null;

    public pickAvatarBtn: HTMLButtonElement | null;

    public selectedFile: File | null;

    public isUploading: boolean;

    public listener: AvatarListener;

    private _pickButtonBound: boolean;

    public constructor(userId: number, modal: HTMLElement) {
        this.userId = userId;
        this.modal = modal;
        this.avatarInput = avatarForm.getAvatarInput(modal);
        this.avatarPreview = avatarForm.getAvatarPreview(modal);
        this.saveAvatarBtn = avatarForm.getSaveButton(modal);
        this.pickAvatarBtn = avatarForm.getPickButton(modal);
        this._pickButtonBound = false;
        this.selectedFile = null;
        this.isUploading = false;

        this.listener = new AvatarListener({
            onFileChange: (event: Event) => this.handleFileSelect(event),
            onSave: () => void this.handleSave(),
            onOpen: () => this.openModal(),
            onClose: () => this.closeModal(),
        });
    }

    public init(): void {
        if (!this.avatarInput) return;

        this.listener.bind({
            avatarInput: this.avatarInput,
            saveAvatarBtn: this.saveAvatarBtn,
            openBtn: avatarForm.getOpenButton(document),
            closeBtn: avatarForm.getCloseButton(this.modal),
            cancelBtn: avatarForm.getCancelButton(this.modal),
            overlay: avatarForm.getOverlay(this.modal),
        });

        if (!this._pickButtonBound && this.pickAvatarBtn instanceof HTMLButtonElement) {
            this._pickButtonBound = true;
            AvatarListener.bindPickButtonClick(this.pickAvatarBtn, (event: MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();
                this.avatarInput?.click();
            });
        }
    }

    public handleFileSelect(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) {
            return;
        }

        const file = target.files?.[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            notifier.error(translator.getTranslation("error_select_image_only", "users"));
            return;
        }

        // The server compresses uploads down to its stored-size target (see
        // AvatarService::uploadAvatar / ImageHelper::compressToTargetSize), so
        // this is just a sane upper bound against accidental huge uploads —
        // keep it in sync with media.avatar.max_upload_bytes server-side.
        if (file.size > 10 * 1024 * 1024) {
            notifier.error(translator.getTranslation("error_file_too_large", "users"));
            return;
        }

        this.selectedFile = file;
        AvatarRender.previewFileToImage(this.avatarPreview, file);
        void this.handleSave();
    }

    public async handleSave(): Promise<void> {
        if (this.isUploading) return;
        if (!this.selectedFile) {
            notifier.error(translator.getTranslation("error_select_image_first", "users"));
            return;
        }

        this.isUploading = true;

        this.setLoadingState(true);
        try {
            const response = await AvatarApi.uploadAvatar(this.selectedFile);
            const data = (await response.json().catch(() => ({}))) as AvatarResponse | AvatarErrorResponse;

            if (!response.ok) {
                notifier.error(String(data.message || translator.getTranslation("error_cannot_upload", "users")));
                return;
            }

            const successData = data as AvatarResponse;

            if (successData.success || successData.avatar_url) {
                const avatarUrl = successData.avatar_url;
                const filename = successData.filename;
                const finalUrl = AvatarRender.buildFinalAvatarUrl(filename, avatarUrl);
                if (finalUrl) {
                    AvatarRender.updateAvatarImages(this.userId, finalUrl);
                }
                this.closeModal();
                notifier.success(translator.getTranslation("success_avatar_updated", "users"), {
                    translateKey: "success_avatar_updated",
                });
                return;
            }

            notifier.error(String(data.message || translator.getTranslation("error_cannot_upload", "users")));
        } catch (error) {
            console.error("Upload error:", error);
            notifier.error(translator.getTranslation("error_upload_failed", "users"));
        } finally {
            this.setLoadingState(false);
            this.isUploading = false;
        }
    }

    public openModal(): void {
        ModalNestedPosition.openModal({
            modalEl: this.modal,
            displayValue: "block",
        });
        ScrollLock.lock();
    }

    public closeModal(): void {
        const bootstrap = Reflect.get(globalThis, "bootstrap") as BootstrapGlobal | undefined;
        const modalApi = bootstrap && typeof bootstrap === "object" ? Reflect.get(bootstrap, "Modal") : null;
        const getInstance = modalApi && typeof modalApi === "object" ? Reflect.get(modalApi, "getInstance") : null;
        const modal = typeof getInstance === "function" ? getInstance.call(modalApi, this.modal) : null;
        const hide = modal && typeof modal === "object" ? Reflect.get(modal, "hide") : null;
        if (typeof hide === "function") {
            hide.call(modal);
        } else {
            ModalNestedPosition.closeModal({ modalEl: this.modal });
        }

        ScrollLock.unlock();
        this.resetModal();
    }

    public setLoadingState(_isLoading: boolean): void {
        // Hook point if we later want a loader; keep behavior unchanged.
    }

    public resetModal(): void {
        this.selectedFile = null;
        if (this.avatarInput) {
            this.avatarInput.value = "";
        }
        if (this.avatarPreview instanceof HTMLImageElement) {
            const originalSrc = this.avatarPreview.getAttribute("data-original-src");
            if (originalSrc) {
                this.avatarPreview.src = originalSrc;
            }
        }
    }
}
